import React, { useEffect } from 'react';
import AuthLayout from '../Auth/AuthLayout/AuthLayout';
import useQueryParams from '../../hooks/useQueryParams';
import IntegrationService from '../../services/common/IntegrationService';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../reducers/store';
import IntegrationActions from '../../actions/IntegrationActions';
import { useTranslate } from 'react-translate.ts';
import IntegrationSelector from '../../selectors/IntegrationSelector';
import AuthCard from '../../components/auth/AuthCard/AuthCard';
import AuthControls from '../Auth/AuthControls/AuthControls';
import { Button, ButtonVariants } from '@tcc-online/common-components';
import styles from './IntegrationPage.module.scss';
import { AuthActions } from '../../actions';
import AuthSelectors from '../../selectors/AuthSelectors';
import { SIGN_IN_PATH } from '../../constants/route.constants';
import useIntegrationAsset from '../../hooks/useIntegrationAsset';
import IIntegration from '../../types/integration/IIntegration';
import FolderPlus from 'react-bootstrap-icons/dist/icons/folder-plus';
import EnvelopeAt from 'react-bootstrap-icons/dist/icons/envelope-at';
import PersonPlus from 'react-bootstrap-icons/dist/icons/person-plus';

const INTEGRATOR_QUERY_KEY = 'integrator';
const COMPANY_ID_QUERY_KEY = 'companyId';
const REDIRECT_QUERY_KEY = 'redirect';

export default function IntegrationPage() {
    const query = useQueryParams();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const translate = useTranslate();
    const {
        data,
        loading,
        error,
    }: { data: IIntegration; loading: boolean; error: boolean } = useSelector(
        IntegrationSelector.getIntegration,
    );
    const isLogged = AuthSelectors.useIsLogged();
    const isAdmin = useSelector(AuthSelectors.isAdmin);

    useEffect(() => {
        if (query?.size > 0) {
            IntegrationService.setParams(query.toString());

            const params = createParams(query);
            if (!params) {
                return;
            }

            dispatch(IntegrationActions.getIntegration(params));
        }
    }, [query]);

    if (error || !isAdmin) {
        dispatch(AuthActions.logout());
    }

    useEffect(() => {
        if (!isLogged) {
            navigate(SIGN_IN_PATH, { replace: true, relative: 'path' });
        }
    }, [isLogged]);

    return (
        <AuthLayout>
            <AuthCard isLoading={loading}>{render()}</AuthCard>
        </AuthLayout>
    );

    function render() {
        const { asset } = useIntegrationAsset(data?.integrator);

        return (
            <>
                {asset && (
                    <img
                        className={styles.logo}
                        src={asset}
                        alt={data?.integrator}
                    />
                )}
                <div className={styles.list}>
                    {data?.permissions.map((permission, index: number) => (
                        <ListItem
                            key={index}
                            text={permission.description}
                            iconName={permission.icon}
                        />
                    ))}
                </div>
                <AuthControls>
                    <Button
                        variant={ButtonVariants.negative}
                        onClick={finalize}
                    >
                        {translate('fe.button.cancel')}
                    </Button>
                    <Button variant={ButtonVariants.positive} onClick={confirm}>
                        {translate('fe.button.allow-integration')}
                    </Button>
                </AuthControls>
            </>
        );
    }

    async function confirm() {
        if (query?.size <= 0) {
            return;
        }

        try {
            const params = createParams(query);
            if (!params) {
                return;
            }

            await dispatch(
                IntegrationActions.enableIntegration(params),
            ).unwrap();
        } finally {
            finalize();
        }
    }

    function createParams(search: URLSearchParams) {
        const integrator = search.get(INTEGRATOR_QUERY_KEY);
        const companyId = search.get(COMPANY_ID_QUERY_KEY);
        if (!integrator || !companyId) {
            return undefined;
        }

        return {
            integrator,
            companyId,
        };
    }

    function finalize() {
        IntegrationService.removeParams();
        const redirectQuery = query.get(REDIRECT_QUERY_KEY);
        if (redirectQuery) {
            window.location.replace(redirectQuery);
        }
    }
}

type ListItemProps = {
    iconName: string;
    text: string;
};

function ListItem(props: ListItemProps) {
    const { iconName, text } = props;

    const Icon = resolveIcon(iconName);

    return (
        <div className={styles.item}>
            {Icon && Icon}
            <span>{text}</span>
        </div>
    );
}

function resolveIcon(iconName: string) {
    switch (iconName) {
        case 'folder-plus':
            return <FolderPlus />;
        case 'envelope-at':
            return <EnvelopeAt />;
        case 'person-plus':
            return <PersonPlus />;
        default:
            return (
                <svg viewBox="0 0 20 20" className={styles.dot}>
                    <circle cx="10" cy="10" r="10" />
                </svg>
            );
    }
}
